/**
 * CookieConsent — Bandeau de consentement conforme RGPD / loi camerounaise
 * sur la protection des données personnelles.
 *
 * Exigences légales couvertes :
 *   1. Trois boutons visuellement équivalents : "Tout accepter",
 *      "Tout refuser" et "Personnaliser" (choix libre et non biaisé).
 *   2. Cases décochées par défaut dans le mode Personnaliser (consentement
 *      spécifique et non ambigu).
 *   3. Traçabilité : chaque décision est journalisée côté serveur
 *      (`/api/consents/log`) avec IP, user-agent, timestamp et version
 *      de la politique.
 *   4. Persistance locale (localStorage) pour ne pas re-demander à chaque
 *      visite, mais re-demande automatiquement si POLICY_VERSION change.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import axios from 'axios';

const STORAGE_KEY = 'realty237.cookie-consent';
export const POLICY_VERSION = '2026-02-26';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Catégories exposées à l'utilisateur — doit rester aligné avec ALLOWED_CATEGORIES
// côté backend (`routes/consent_routes.py`).
const CATEGORIES = [
  {
    key: 'necessary',
    label: 'Cookies nécessaires',
    description:
      'Indispensables au fonctionnement du site : authentification, panier, sécurité. Ne peuvent pas être désactivés.',
    locked: true,
  },
  {
    key: 'analytics',
    label: 'Mesure d\u2019audience',
    description:
      'Statistiques anonymes de navigation (PostHog) pour améliorer l\u2019expérience. Aucune donnée revendue.',
    locked: false,
  },
  {
    key: 'marketing',
    label: 'Marketing & publicité',
    description:
      'Personnalisation des offres commerciales et remarketing. Vous pouvez retirer votre consentement à tout moment.',
    locked: false,
  },
  {
    key: 'functional',
    label: 'Préférences (fonctionnel)',
    description:
      'Mémorise vos réglages (thème sombre, langue, filtres de recherche…) pour une expérience plus fluide.',
    locked: false,
  },
];

const DEFAULT_PREFS = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const readConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeConsent = (prefs) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ prefs, ts: Date.now(), version: POLICY_VERSION }),
    );
  } catch {
    /* quota */
  }
};

// Best-effort remote log — never bloque l'UX si le backend est offline.
const logConsentRemote = (prefs, source) => {
  const payload = { consents: prefs, source, policy_version: POLICY_VERSION };
  axios.post(`${API}/consents/log`, payload, { withCredentials: true }).catch(() => {});
};

const CookieConsent = () => {
  const [show, setShow] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  // Mobile bottom-offset — dynamically avoids overlap with:
  //   • MobileBottomTabBar (Dashboard) — 56px + safe-area
  //   • FilterFab (Properties) — 44 + 20 gap = 64px above tab bar
  //   • PropertyStickyCta (Detail page) — 68px + safe-area
  // Recomputed on mount + resize + navigation so the banner always sits
  // 12px above the topmost bottom-fixed element on the current page.
  const [bottomOffset, setBottomOffset] = useState(16);

  useEffect(() => {
    if (!readConsent()) {
      const t = setTimeout(() => setShow(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (!show) return undefined;
    const compute = () => {
      // Only apply extra offset on mobile viewports (< md breakpoint).
      if (window.innerWidth >= 768) { setBottomOffset(16); return; }
      const selectors = [
        '[data-testid="mobile-bottom-tab-bar"]',
        '[data-testid="filters-mobile-fab"]',
        '[data-testid="property-sticky-cta"]',
      ];
      let maxAbove = 0;
      selectors.forEach((sel) => {
        const el = document.querySelector(sel);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const above = Math.max(0, window.innerHeight - rect.top);
        if (above > maxAbove) maxAbove = above;
      });
      setBottomOffset(maxAbove > 0 ? maxAbove + 12 : 16);
    };
    compute();
    window.addEventListener('resize', compute);
    // Recompute on route change (SPA navigation) via a lightweight interval
    // — cheaper than wiring a Router listener from a non-routed component.
    const iv = setInterval(compute, 700);
    return () => {
      window.removeEventListener('resize', compute);
      clearInterval(iv);
    };
  }, [show]);

  const finalize = (nextPrefs) => {
    writeConsent(nextPrefs);
    logConsentRemote(nextPrefs, customize ? 'preference_center' : 'banner');
    window.dispatchEvent(
      new CustomEvent('realty237:cookie-consent', {
        detail: { prefs: nextPrefs, version: POLICY_VERSION },
      }),
    );
    setShow(false);
    setCustomize(false);
  };

  const acceptAll = () => {
    finalize({ necessary: true, analytics: true, marketing: true, functional: true });
  };
  const rejectAll = () => {
    finalize({ necessary: true, analytics: false, marketing: false, functional: false });
  };
  const savePrefs = () => finalize({ ...prefs, necessary: true });

  const openCustomize = () => setCustomize(true);
  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const bannerButtonBase = useMemo(
    () =>
      'flex-1 sm:flex-initial min-w-[7.5rem] rounded-full font-medium transition-transform hover:-translate-y-0.5',
    [],
  );

  if (!show) return null;

  if (customize) {
    return (
      <div
        className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-3 sm:p-6 animate-in fade-in duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-custom-title"
        data-testid="cookie-consent-customize"
      >
        <div className="w-full max-w-lg bg-white dark:bg-stone-900 rounded-2xl shadow-2xl overflow-hidden border border-stone-200 dark:border-stone-800 animate-in slide-in-from-bottom-6 duration-300">
          <div className="flex items-start justify-between p-5 border-b border-stone-100 dark:border-stone-800">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <h2 id="cookie-custom-title" className="text-base font-semibold text-stone-900 dark:text-stone-100">
                  Personnaliser mes préférences
                </h2>
                <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
                  Version {POLICY_VERSION} — vous pouvez modifier vos choix à tout moment.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setCustomize(false)}
              aria-label="Fermer"
              className="text-stone-400 hover:text-stone-600 p-1"
              data-testid="cookie-customize-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[55vh] overflow-y-auto p-5 space-y-4">
            {CATEGORIES.map((cat) => (
              <div
                key={cat.key}
                className="flex items-start justify-between gap-4 p-3 rounded-xl border border-stone-100 dark:border-stone-800 bg-stone-50/40 dark:bg-stone-800/40"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-stone-900 dark:text-stone-100">
                      {cat.label}
                    </p>
                    {cat.locked && (
                      <span className="text-label uppercase tracking-wider bg-stone-200 text-stone-600 dark:bg-stone-700 dark:text-stone-300 px-1.5 py-0.5 rounded-full">
                        toujours actif
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-stone-600 dark:text-stone-400 mt-1 leading-relaxed">
                    {cat.description}
                  </p>
                </div>
                <Switch
                  checked={cat.locked ? true : !!prefs[cat.key]}
                  disabled={cat.locked}
                  onCheckedChange={() => !cat.locked && toggle(cat.key)}
                  aria-label={`Activer ${cat.label}`}
                  data-testid={`cookie-toggle-${cat.key}`}
                />
              </div>
            ))}
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Voir aussi notre{' '}
              <Link to="/privacy#cookies" className="text-brand-600 hover:underline">
                politique de confidentialité
              </Link>
              .
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 p-4 border-t border-stone-100 dark:border-stone-800 bg-stone-50/60 dark:bg-stone-900/60">
            <Button
              variant="outline"
              onClick={rejectAll}
              className="flex-1"
              data-testid="cookie-reject-all-from-customize"
            >
              Tout refuser
            </Button>
            <Button
              onClick={savePrefs}
              className="flex-1 bg-brand-600 hover:bg-brand-700 text-white"
              data-testid="cookie-save-preferences"
            >
              Enregistrer mes choix
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-3xl
                 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 shadow-2xl rounded-2xl
                 animate-in fade-in slide-in-from-bottom-4 duration-300"
      style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)` }}
      data-testid="cookie-consent"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      aria-live="polite"
    >
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
        <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600">
          <Cookie className="h-5 w-5" />
        </div>
        <div className="flex-1 text-sm text-stone-700 dark:text-stone-300 leading-relaxed">
          <p id="cookie-consent-title" className="font-semibold text-stone-900 dark:text-stone-100 mb-0.5">
            Cookies &amp; confidentialité
          </p>
          <p id="cookie-consent-desc">
            Nous utilisons des cookies essentiels au fonctionnement du site et, sous réserve de votre
            consentement, des cookies de mesure d&apos;audience et de personnalisation.
            {' '}
            <Link
              to="/privacy#cookies"
              className="text-brand-600 hover:underline font-medium"
              data-testid="cookie-consent-link"
            >
              En savoir plus
            </Link>
            .
          </p>
        </div>
      </div>

      {/* 3 boutons strictement équivalents (même variante, même taille,
          même style) — exigence RGPD : le refus doit être aussi facile et
          visuellement neutre que l'acceptation. */}
      <div className="flex flex-col sm:flex-row gap-2 px-4 pb-4 sm:px-5 sm:pb-5">
        <Button
          variant="outline"
          size="default"
          onClick={rejectAll}
          data-testid="cookie-reject-all"
          className={bannerButtonBase}
        >
          Tout refuser
        </Button>
        <Button
          variant="outline"
          size="default"
          onClick={openCustomize}
          data-testid="cookie-customize"
          className={bannerButtonBase}
        >
          Personnaliser
        </Button>
        <Button
          variant="outline"
          size="default"
          onClick={acceptAll}
          data-testid="cookie-accept-all"
          className={bannerButtonBase}
        >
          Tout accepter
        </Button>
      </div>
    </div>
  );
};

export default CookieConsent;
